import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { usePermissions } from "hooks/usePermissions"
import { getBlackouts, createBlackout, updateBlackout, deleteBlackout } from "store/blackouts"
import { BLACKOUT_FILTERS_INITIAL, BLACKOUT_SCOPES, ROW_MENU_ACTIONS } from "../constants"
import { buildBlackoutListParams } from "../utils"

/**
 * Owns the blackout list plus the add/edit/delete modal state for the settings page.
 * Mirrors the shape of `Settings/AddOns/hooks/useAddOns`.
 */
export const useBlackouts = ({ enabled = true } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { hasFullAccessFlag } = usePermissions()

  const items = useSelector((state) => state.blackouts?.items || [])
  const isLoading = useSelector((state) => Boolean(state.blackouts?.loading))
  const isCreating = useSelector((state) => Boolean(state.blackouts?.createBlackout?.loading))
  const isUpdating = useSelector((state) => Boolean(state.blackouts?.updateBlackout?.loading))
  const isDeleting = useSelector((state) => Boolean(state.blackouts?.deleteBlackout?.loading))

  // `null` when closed; otherwise { scope, blackout } where blackout is null for "add".
  const [formState, setFormState] = useState(null)
  const [blackoutToDelete, setBlackoutToDelete] = useState(null)
  const [activeResourceTab, setActiveResourceTab] = useState(BLACKOUT_SCOPES.CREW)
  const [filters, setFilters] = useState(BLACKOUT_FILTERS_INITIAL)

  const listParams = useMemo(() => buildBlackoutListParams(filters), [filters])

  /**
   * `api()` rejects on failure and has already shown the error notification by then, so every
   * list fetch swallows it — the reducer's `failed` flag is what the UI reads.
   */
  const refreshList = useCallback(() => dispatch(getBlackouts(listParams)).catch(() => {}), [dispatch, listParams])

  useEffect(() => {
    if (enabled) refreshList()
  }, [enabled, refreshList])

  const byScope = useMemo(
    () => ({
      [BLACKOUT_SCOPES.BUSINESS]: items.filter((item) => item.scope === BLACKOUT_SCOPES.BUSINESS),
      [BLACKOUT_SCOPES.LISTING]: items.filter((item) => item.scope === BLACKOUT_SCOPES.LISTING),
      [BLACKOUT_SCOPES.CREW]: items.filter((item) => item.scope === BLACKOUT_SCOPES.CREW),
      [BLACKOUT_SCOPES.EQUIPMENT]: items.filter((item) => item.scope === BLACKOUT_SCOPES.EQUIPMENT)
    }),
    [items]
  )

  const updateFilters = useCallback((partial) => setFilters((current) => ({ ...current, ...partial })), [])
  const clearFilters = useCallback(() => setFilters(BLACKOUT_FILTERS_INITIAL), [])

  const openAddModal = useCallback((scope) => setFormState({ scope, blackout: null }), [])
  const openEditModal = useCallback((blackout) => setFormState({ scope: blackout.scope, blackout }), [])
  const closeFormModal = useCallback(() => setFormState(null), [])

  /**
   * A failed write leaves its modal open so the guide keeps what they typed, and returns without
   * rejecting — both handlers are called from un-awaited `onClick`s, where a rejection would
   * surface as an unhandled promise rejection rather than anything the user can act on. It
   * resolves to whether the record was saved, which is what a caller needs to decide whether to
   * follow the save with anything of its own.
   */
  const handleSubmit = useCallback(
    async (payload) => {
      try {
        if (formState?.blackout) await dispatch(updateBlackout(formState.blackout.id, payload))
        else await dispatch(createBlackout(payload))
      } catch {
        return false
      }
      await refreshList()
      setFormState(null)
      return true
    },
    [dispatch, formState, refreshList]
  )

  const handleDelete = useCallback(async () => {
    if (!blackoutToDelete) return
    try {
      await dispatch(deleteBlackout(blackoutToDelete.id))
    } catch {
      return
    }
    await refreshList()
    setBlackoutToDelete(null)
  }, [dispatch, blackoutToDelete, refreshList])

  /**
   * Only the guide/admin manages blackouts, so crew and staff get no menu. Crew time off listed
   * here is edited from the crew member's profile, not as a blackout.
   */
  const getMenuItems = useCallback(
    (blackout) => (!hasFullAccessFlag || blackout.entry_type === "time_off" ? [] : [
      {
        key: ROW_MENU_ACTIONS.EDIT,
        label: t("settings.action_edit", { defaultValue: "Edit" }),
        onClick: () => openEditModal(blackout)
      },
      {
        key: ROW_MENU_ACTIONS.DELETE,
        label: t("settings.action_delete", { defaultValue: "Delete" }),
        danger: true,
        onClick: () => setBlackoutToDelete(blackout)
      }
    ]),
    [hasFullAccessFlag, openEditModal, t]
  )

  return {
    items,
    byScope,
    isLoading,
    isSaving: isCreating || isUpdating,
    isDeleting,
    formState,
    openAddModal,
    openEditModal,
    closeFormModal,
    handleSubmit,
    blackoutToDelete,
    setBlackoutToDelete,
    handleDelete,
    refreshList,
    getMenuItems,
    activeResourceTab,
    setActiveResourceTab,
    filters,
    updateFilters,
    clearFilters
  }
}

export default useBlackouts
